import React, { useMemo, useState } from 'react';
import { CameraIcon, LinkIcon } from 'lucide-react';
import { useData } from '../../../store/DataContext';
import { useHrPayroll } from '../../../contexts/HrPayrollContext';
import { employmentTypes } from '../../../data/hrDefaults';
import { EmployeeInput, HrEmployee } from '../../../types/hr';
import { Button } from '../../../components/ui/Button';
import { HrModal } from './HrModal';
import { PayItemsEditor } from './PayItemsEditor';
type EmployeeFormProps = {
  employee?: HrEmployee;
  onClose: () => void;
  onSaved: (id: string) => void;
};
const emptyInput: EmployeeInput = {
  firstName: '',
  middleName: '',
  surname: '',
  sex: 'Female',
  dateOfBirth: '',
  idNumber: '',
  nationality: 'Zambian',
  maritalStatus: '',
  address: '',
  phone: '',
  email: '',
  nextOfKinName: '',
  nextOfKinRelationship: '',
  nextOfKinPhone: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  departmentId: '',
  positionId: '',
  employmentType: 'Permanent',
  startDate: '',
  contractExpiry: '',
  supervisorId: '',
  status: 'Active',
  professionalRegNumber: '',
  licenceExpiry: '',
  basicSalary: 0,
  salaryGrade: '',
  paymentFrequency: 'Monthly',
  bankName: '',
  bankAccountName: '',
  bankAccountNumber: '',
  bankBranch: '',
  napsaNumber: '',
  nhimaNumber: '',
  tpin: '',
  earnings: [],
  deductions: []
};
const inputClass = 'mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

/** Register a new employee or edit personal / employment / bank details. */
export function EmployeeForm({
  employee,
  onClose,
  onSaved
}: EmployeeFormProps) {
  const {
    users
  } = useData();
  const {
    employees,
    departments,
    positions,
    settings,
    registerEmployee,
    updateEmployee
  } = useHrPayroll();
  const isEdit = Boolean(employee);
  const [form, setForm] = useState<EmployeeInput>(() => {
    if (!employee) return emptyInput;
    const {
      id: _id,
      employeeNumber: _n,
      salaryHistory: _h,
      events: _e,
      createdAt: _c,
      createdByName: _cb,
      updatedAt: _u,
      ...rest
    } = employee;
    return {
      ...emptyInput,
      ...rest
    };
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof EmployeeInput,>(key: K, value: EmployeeInput[K]) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const linkedUserIds = useMemo(() => new Set(employees.filter(e => e.userId && e.id !== employee?.id).map(e => e.userId!)), [employees, employee]);
  const linkableUsers = users.filter(u => !linkedUserIds.has(u.id));
  const deptPositions = positions.filter(p => p.departmentId === form.departmentId);
  const handlePhoto = (file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const size = 240;
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, size / Math.max(img.width, img.height));
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
        set('photo', canvas.toDataURL('image/jpeg', 0.82));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  };
  const linkUser = (userId: string) => {
    set('userId', userId || undefined);
    const user = users.find(u => u.id === userId);
    if (user && !form.firstName && !form.surname) {
      const parts = user.fullName.trim().split(/\s+/);
      setForm(prev => ({
        ...prev,
        userId,
        firstName: parts[0] ?? '',
        surname: parts.slice(1).join(' ')
      }));
    }
  };
  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.firstName.trim()) next.firstName = 'Required';
    if (!form.surname.trim()) next.surname = 'Required';
    if (!form.departmentId) next.departmentId = 'Required';
    if (!form.positionId) next.positionId = 'Required';
    if (!form.startDate) next.startDate = 'Required';
    if (!isEdit && !(form.basicSalary > 0)) next.basicSalary = 'Enter the basic monthly salary';
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Invalid email';
    if (form.contractExpiry && form.startDate && form.contractExpiry < form.startDate) next.contractExpiry = 'Before start date';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaveError('');
    if (!validate()) {
      setSaveError('Please correct the highlighted fields.');
      return;
    }
    setSaving(true);
    // Salary and status are changed through their own audited workflows.
    const {
      basicSalary: _b,
      earnings: _e,
      deductions: _d,
      salaryGrade: _g,
      status: _s,
      ...profilePatch
    } = form;
    const result = isEdit ? await updateEmployee(employee!.id, profilePatch, 'Profile details updated') : await registerEmployee(form);
    setSaving(false);
    if (!result.ok) return setSaveError(result.error ?? 'Could not save.');
    onSaved(result.id!);
  };
  const field = (key: keyof EmployeeInput, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => <div data-id="element-461" data-mp="3m1hmirfvh16f">
      <label htmlFor={`emp-${key}`} className="block text-sm font-medium text-slate-700" data-id="element-462" data-mp="3m17r2oc261tc">{label}{props.required && <span className="text-red-600" data-id="element-463" data-mp="3v02z85ynxlza"> *</span>}</label>
      <input id={`emp-${key}`} value={form[key] as string | number | undefined ?? ''} onChange={e => set(key, (props.type === 'number' ? Number(e.target.value) : e.target.value) as never)} aria-invalid={Boolean(errors[key])} className={`${inputClass} ${errors[key] ? 'border-red-400' : ''}`} {...props} data-id="element-464" data-mp="3uyi4af8fs10w" />
      {errors[key] && <p className="mt-1 text-xs text-red-700" data-id="element-465" data-mp="2al9fodter3oy">{errors[key]}</p>}
    </div>;
  const select = (key: keyof EmployeeInput, label: string, options: {
    value: string;
    label: string;
  }[], required = false, onChange?: (value: string) => void) => <div data-id="element-466" data-mp="2vbdegx1lewuf">
      <label htmlFor={`emp-${key}`} className="block text-sm font-medium text-slate-700" data-id="element-467" data-mp="2vbn4gkmn1404">{label}{required && <span className="text-red-600" data-id="element-468" data-mp="349yr6bkdvhna"> *</span>}</label>
      <select id={`emp-${key}`} value={form[key] as string | undefined ?? ''} onChange={e => onChange ? onChange(e.target.value) : set(key, e.target.value as never)} className={`${inputClass} bg-white ${errors[key] ? 'border-red-400' : ''}`} data-id="element-469" data-mp="3497983jplifh">
        <option value="" data-id="element-470" data-mp="3i8oqlxuodtet">{required ? 'Select…' : '— None —'}</option>
        {options.map(o => <option key={o.value} value={o.value} data-id="element-471" data-mp="3n06qeikypny7">{o.label}</option>)}
      </select>
      {errors[key] && <p className="mt-1 text-xs text-red-700" data-id="element-472" data-mp="3rzt2gop20y5f">{errors[key]}</p>}
    </div>;
  return <HrModal title={isEdit ? `Edit ${employee!.employeeNumber}` : 'Register new employee'} subtitle={isEdit ? 'Salary changes are made from the Salary & Benefits tab so history is kept.' : 'An employee number is assigned automatically on save.'} onClose={onClose} size="xl" footer={<>
          {saveError && <p role="alert" className="mr-auto self-center text-sm text-red-700" data-id="element-474" data-mp="1l9ptrywkbqa0">{saveError}</p>}
          <Button type="button" variant="outline" onClick={onClose} data-id="element-475" data-mp="1gcpv4921lpsx">Cancel</Button>
          <Button type="submit" form="employee-form" isLoading={saving} data-id="element-476" data-mp="1be661zs5jxwc">{isEdit ? 'Save changes' : 'Register employee'}</Button>
        </>} data-id="element-473" data-mp="tryf03ib4ta">
      <form id="employee-form" onSubmit={submit} className="space-y-6" noValidate data-id="element-477" data-mp="292pvpvpiggpz">
        <section className="rounded-lg border border-blue-100 bg-blue-50/50 p-4" data-id="element-478" data-mp="243va6m05se26">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800" data-id="element-479" data-mp="1z9h8y2ytfcpl">
            <LinkIcon className="h-4 w-4 text-blue-600" aria-hidden="true" data-id="element-480" data-mp="1ub7clp1cu62e" /> Existing system account
          </div>
          <p className="mb-2 text-xs text-slate-600" data-id="element-481" data-mp="2rsnzw0q6tpoc">Link this HR profile to the staff member’s existing HMS login. Their login and dashboard access are not changed.</p>
          <select value={form.userId ?? ''} onChange={e => linkUser(e.target.value)} className={`${inputClass} bg-white`} aria-label="Linked system account" data-id="element-482" data-mp="1vc0gp0giqjq0">
            <option value="" data-id="element-483" data-mp="1mcdcy80bh1ts">No system account (e.g. housekeeping, security)</option>
            {linkableUsers.map(u => <option key={u.id} value={u.id} data-id="element-484" data-mp="1rayhgjmlq7o1">{u.fullName} — {u.role}</option>)}
          </select>
        </section>

        <section data-id="element-485" data-mp="2f0sub16x7w8a">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500" data-id="element-486" data-mp="2f2dm4j1up12g">Personal information</h3>
          <div className="flex flex-col gap-5 md:flex-row" data-id="element-487" data-mp="2j24cilngtf6g">
            <div className="flex shrink-0 flex-col items-center gap-2" data-id="element-488" data-mp="253o3up61lelo">
              <div className="flex h-28 w-24 items-center justify-center overflow-hidden rounded-md border border-slate-300 bg-slate-50" data-id="element-489" data-mp="2a1zc4vgovh1s">
                {form.photo ? <img src={form.photo} alt="Employee passport photograph" className="h-full w-full object-cover" data-id="element-490" data-mp="tmy1jh4qp6xc" /> : <CameraIcon className="h-8 w-8 text-slate-300" aria-hidden="true" data-id="element-491" data-mp="yodqvldy9zlj" />}
              </div>
              <label className="cursor-pointer text-xs font-medium text-blue-700 hover:underline" data-id="element-492" data-mp="r1qeqisvbpoi">
                {form.photo ? 'Change photo' : 'Upload photo'}
                <input type="file" accept="image/*" className="sr-only" onChange={e => handlePhoto(e.target.files?.[0])} data-id="element-493" data-mp="104hp0fa0cfek" />
              </label>
            </div>
            <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-494" data-mp="v3272w34mp0e">
              {field('firstName', 'First name', {
              required: true
            })}
              {field('middleName', 'Middle name')}
              {field('surname', 'Surname', {
              required: true
            })}
              {select('sex', 'Sex', [{
              value: 'Female',
              label: 'Female'
            }, {
              value: 'Male',
              label: 'Male'
            }], true)}
              {field('dateOfBirth', 'Date of birth', {
              type: 'date'
            })}
              {field('idNumber', 'NRC / passport number')}
              {field('nationality', 'Nationality')}
              {select('maritalStatus', 'Marital status', ['Single', 'Married', 'Divorced', 'Widowed'].map(v => ({
              value: v,
              label: v
            })))}
              {field('phone', 'Phone number', {
              type: 'tel'
            })}
              {field('email', 'Email address', {
              type: 'email'
            })}
              <div className="sm:col-span-2" data-id="element-495" data-mp="2net8csse5dl7">{field('address', 'Residential address')}</div>
              {field('nextOfKinName', 'Next of kin')}
              {field('nextOfKinRelationship', 'Relationship')}
              {field('nextOfKinPhone', 'Next of kin phone', {
              type: 'tel'
            })}
              {field('emergencyContactName', 'Emergency contact')}
              {field('emergencyContactPhone', 'Emergency contact phone', {
              type: 'tel'
            })}
            </div>
          </div>
        </section>

        <section data-id="element-496" data-mp="1zqa1mhozp8v8">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500" data-id="element-497" data-mp="1zqt9t6ou8iw2">Employment details</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-498" data-mp="1vmvaovzij830">
            {select('departmentId', 'Department', departments.map(d => ({
            value: d.id,
            label: d.name
          })), true, value => setForm(prev => ({
            ...prev,
            departmentId: value,
            positionId: ''
          })))}
            {select('positionId', 'Designation / job title', deptPositions.map(p => ({
            value: p.id,
            label: p.title
          })), true)}
            {select('employmentType', 'Employment type', employmentTypes.map(t => ({
            value: t,
            label: t
          })), true)}
            {field('startDate', 'Commencement date', {
            type: 'date',
            required: true
          })}
            {field('contractExpiry', 'Contract expiry date', {
            type: 'date'
          })}
            {select('supervisorId', 'Reporting supervisor', employees.filter(e => e.id !== employee?.id && e.status === 'Active').map(e => ({
            value: e.id,
            label: `${e.firstName} ${e.surname} (${e.employeeNumber})`
          })))}
            {!isEdit && select('status', 'Employment status', ['Active', 'Suspended', 'Inactive'].map(v => ({
            value: v,
            label: v
          })), true)}
            {field('professionalRegNumber', 'Professional registration no.')}
            {field('licenceExpiry', 'Licence expiry date', {
            type: 'date'
          })}
          </div>
        </section>

        <section data-id="element-499" data-mp="c0833g33qa2e">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500" data-id="element-500" data-mp="73q1uq6zctut">Payroll details</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-501" data-mp="kumps3pyomvj">
            {!isEdit && field('basicSalary', 'Basic monthly salary (K)', {
            type: 'number',
            min: 0,
            step: '0.01',
            required: true
          })}
            {!isEdit && select('salaryGrade', 'Salary grade', settings.salaryGrades.map(g => ({
            value: g.name,
            label: g.name
          })))}
            {field('bankName', 'Bank name')}
            {field('bankBranch', 'Bank branch')}
            {field('bankAccountName', 'Account name')}
            {field('bankAccountNumber', 'Account number', {
            autoComplete: 'off'
          })}
            {field('napsaNumber', 'NAPSA membership no.')}
            {field('nhimaNumber', 'NHIMA membership no.')}
            {field('tpin', 'TPIN')}
          </div>
          {!isEdit && <div className="mt-4 grid gap-6 md:grid-cols-2" data-id="element-502" data-mp="18mjrv33m00jg">
              <PayItemsEditor label="Fixed monthly allowances" items={form.earnings} types={settings.earningTypes.filter(t => !t.system)} onChange={items => set('earnings', items)} addLabel="Add allowance" data-id="element-503" data-mp="1h0eouo97k48v" />
              <PayItemsEditor label="Fixed monthly deductions" items={form.deductions} types={settings.deductionTypes} onChange={items => set('deductions', items)} addLabel="Add deduction" data-id="element-504" data-mp="174ldv9b50it7" />
            </div>}
        </section>
      </form>
    </HrModal>;
}